import { Box, Checkbox, FormControlLabel, Switch, Typography, IconButton, TextField } from '@material-ui/core';
import { KeyboardDatePicker } from '@material-ui/pickers';
import RefreshIcon from '@material-ui/icons/Refresh';
import { useSnackbar } from 'notistack';
import { extractError } from 'utils/api';
import IntlMessages from '@crema/utility/IntlMessages';
import ConfirmationDialog from '@crema/core/ConfirmationDialog';

import { IRow, MemoizedBasicTable, StringNumberArrayType } from 'components/Table/BasicTable';
import { OfferingFilterForm } from 'containers/Dashboard/Offering/OfferingToolbar';
import usePermissionCheck from 'hooks/usePermissionCheck';
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectDeityAsMapWithUrl } from 'store/reducers/temple/deitySlice';
import { selectOfferingCategoryAsMapWithUrl, TOfferingCategoryResponseMapType } from 'store/reducers/temple/offeringCategorySlice';
import { offeringUpdateThunk, offeringResetTokenReferenceThunk, offeringTimeDeleteThunk, offeringRetrieveThunk } from 'store/reducers/temple/offeringSlice';
import { selectAllAddon } from 'store/reducers/temple/offeringAddonSlice';
import { unwrapResult } from '@reduxjs/toolkit';
import { format as formatDate } from 'date-fns'
import { IOfferingResponse, IOfferingTimeResponse } from 'types/api/temple';
import { capStr } from 'utils/fns';
import { PERMISSIONS, RESTRICTED_PATHS } from 'utils/permissions';
import { fmtBlockPeriod, getTimeChipText, offeringTimeToForm } from 'utils/temple/offering';
import { Edit, Save, Delete as DeleteIcon } from '@material-ui/icons';
import { IAddonResponse } from 'containers/Dashboard/Bookable/AdditionalFields';
import { extractIdFromUrlRegex } from 'shared/constants/AppConst';

interface OfferingTableProps {
    editHandler?: ((id: string | number) => void | Promise<any>) & Function,
    deleteHandler?: ((id: string | number) => void | Promise<any>) & Function,
    deletingIds?: StringNumberArrayType,
    offerings: IOfferingResponse[],
    filterList?: OfferingFilterForm,
    selectedIds?: StringNumberArrayType,
    onSelectOne?: (id: string | number, checked: boolean) => void,
    onSelectAllVisible?: (ids: StringNumberArrayType, checked: boolean) => void,
    onBulkEditTimes?: () => void,
    onBulkEditMedium?: () => void,
    onBulkEditPools?: () => void,
    onBulkEditAddonFields?: () => void,
    onBulkEditCourier?: () => void,
    onBulkEditTags?: () => void,
    onBulkEditTypeDates?: () => void
}

const TempleRefCell = ({ value, row }: { value: string, row: IOfferingResponse }) => {
    const [val, setVal] = useState(value)
    const dispatch = useAppDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const isDirty = val !== value

    useEffect(() => {
        setVal(value)
    }, [value])

    const handleUpdate = async () => {
        if (!isDirty) return

        try {
            const res = await dispatch(offeringUpdateThunk({
                url: row.url,
                data: { templeRefNo: val }
            }))
            if (offeringUpdateThunk.fulfilled.match(res)) {
                enqueueSnackbar('Updated successfully', { variant: 'success' })
            } else {
                setVal(value)
                const err = res.payload
                enqueueSnackbar(extractError(err) ?? 'Failed to update', { variant: 'error' })
            }
        } catch (e: any) {
            setVal(value)
            enqueueSnackbar(extractError(e) ?? 'Update failed', { variant: 'error' })
        }
    }

    return (
        <Box display="flex" alignItems="center">
            <TextField
                value={val}
                onChange={(e) => setVal(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        handleUpdate()
                    }
                }}
                fullWidth
                placeholder="Ref No"
                InputProps={{
                    style: { fontSize: 'inherit' }
                }}
            />
            {isDirty && (
                <IconButton size="small" onClick={handleUpdate} style={{ marginLeft: 4 }}>
                    <Save fontSize="small" />
                </IconButton>
            )}
        </Box>
    )
}

const HeaderAction = ({
    label,
    onClick,
    disabled
}: {
    label: React.ReactNode,
    onClick?: () => void,
    disabled?: boolean
}) => (
    <Box display="inline-flex" alignItems="center">
        <Typography variant="body2" component="span">{label}</Typography>
        {onClick && (
            <IconButton
                size="small"
                aria-label="bulk edit"
                onClick={onClick}
                disabled={disabled}
            >
                <Edit fontSize="small" />
            </IconButton>
        )}
    </Box>
)

const addonTypeLabels: Record<string, string> = {
    str: 'Text',
    int: 'Number(Int)',
    float: 'Number(Float)'
}

const isAddonForOffering = (field: IAddonResponse, offering: IOfferingResponse) =>
    field.offering === offering.url || field.offering.match(extractIdFromUrlRegex)?.[1] === String(offering.id)

const OfferingTable: React.FC<OfferingTableProps> = ({
    editHandler,
    deleteHandler,
    deletingIds,
    offerings,
    filterList,
    selectedIds = [],
    onSelectOne,
    onSelectAllVisible,
    onBulkEditTimes,
    onBulkEditMedium,
    onBulkEditPools,
    onBulkEditAddonFields,
    onBulkEditCourier,
    onBulkEditTags,
    onBulkEditTypeDates
}) => {
    const dispatch = useAppDispatch()
    const hasEditPermission = usePermissionCheck(RESTRICTED_PATHS.OFFERINGS, PERMISSIONS.CHANGE)
    const categoryMap = useAppSelector(selectOfferingCategoryAsMapWithUrl)
    const deityMap = useAppSelector(selectDeityAsMapWithUrl)
    const addonFields = useAppSelector(selectAllAddon)

    const [selectedDates, setSelectedDates] = useState<Record<string, Date | null>>({})
    const [deletingTime, setDeletingTime] = useState<{ url: string, offeringId: string | number } | null>(null)
    const { enqueueSnackbar } = useSnackbar()

    const confirmDeleteTime = useCallback(async () => {
        if (!deletingTime) return
        try {
            await dispatch(offeringTimeDeleteThunk({ url: deletingTime.url })).then(unwrapResult)
            enqueueSnackbar('Offering time removed', { variant: 'success' })
            dispatch(offeringRetrieveThunk({ id: deletingTime.offeringId }))
        } catch (e: any) {
            enqueueSnackbar(extractError(e) ?? 'Failed to remove offering time', { variant: 'error' })
        } finally {
            setDeletingTime(null)
        }
    }, [dispatch, enqueueSnackbar, deletingTime])

    const handleDeleteTime = useCallback((timeUrl: string, offeringId: string | number) => {
        setDeletingTime({ url: timeUrl, offeringId })
    }, [])

    const handleToggleCourier = useCallback(async (offering: IOfferingResponse) => {
        try {
            const res = await dispatch(offeringUpdateThunk({
                url: offering.url,
                data: { isCourierAvailable: !offering.isCourierAvailable }
            }))
            if (offeringUpdateThunk.fulfilled.match(res)) {
                enqueueSnackbar('Courier status updated successfully', { variant: 'success' })
            } else {
                const err = res.payload ?? res.error
                enqueueSnackbar(extractError(err) ?? 'Failed to update courier status', { variant: 'error' })
            }
        } catch (e: any) {
            enqueueSnackbar(extractError(e) ?? 'Failed to update courier status', { variant: 'error' })
        }
    }, [dispatch, enqueueSnackbar])

    const visibleOfferings = useMemo(() => offerings.filter(offering => (
        (!filterList?.category || offering.category === filterList?.category) &&
        (!filterList?.deity || offering.assignedDeities.some(d => d === filterList.deity))
    )), [offerings, filterList])

    const visibleIds = useMemo(() => visibleOfferings.map(offering => offering.id), [visibleOfferings])
    const selectedVisibleCount = useMemo(
        () => visibleIds.filter(id => selectedIds.includes(id)).length,
        [selectedIds, visibleIds]
    )

    const getRows = useCallback((
        offerings: IOfferingResponse[],
        handleToggle: (offering: IOfferingResponse) => void,
        categoryMap: TOfferingCategoryResponseMapType
    ): IRow[] => {
        let rows: IRow[] = []
        offerings.forEach((offering, i) => {
            const row = {
                id: offering.id,
                values: [
                    <Box display="flex" alignItems="center">
                        <Checkbox
                            size="small"
                            checked={selectedIds.includes(offering.id)}
                            onChange={event => onSelectOne?.(offering.id, event.target.checked)}
                        />
                        <Typography variant="body2">{i + 1}</Typography>
                    </Box>,
                    hasEditPermission ? <TempleRefCell value={offering.templeRefNo ?? ""} row={offering} /> : (offering.templeRefNo ?? "N/A"),
                    offering.name,
                    categoryMap[offering?.category]?.name,
                    offering.price,
                    (offering.assignedDeities.map(d => deityMap[d]?.name)?.join(', ') ?? "N/A"),
                    capStr(offering.offeringType),
                    ((offering.availableOnline && (offering.availableCounter || offering.availableKiosk || offering.availablePos))
                        ? "All"
                        : ((offering.availableCounter || offering.availableKiosk || offering.availablePos)
                            ? [
                                offering.availableCounter ? "Counter" : null,
                                offering.availablePos ? "POS" : null,
                                offering.availableKiosk ? "Kiosk" : null
                            ].filter(Boolean).join(" | ")
                            : (offering.availableOnline ? "App/Web" : "N/A"))),
                    offering.offeringTimes && offering.offeringTimes.length > 0 ?
                        (() => {
                            const pools = offering.offeringTimes.flatMap(ot => ot.poolConnections.map(pc => pc?.pool?.poolName).filter(Boolean));
                            const uniquePools = Array.from(new Set(pools));
                            return uniquePools.length > 0 ? uniquePools.join(', ') : 'N/A';
                        })()
                    : (offering.poolConnections?.length > 0 ? (() => {
                        const pools = offering.poolConnections.map(pc => pc?.pool?.poolName).filter(Boolean);
                        const uniquePools = Array.from(new Set(pools));
                        return uniquePools.length > 0 ? uniquePools.join(', ') : 'N/A';
                    })() : 'N/A'),
                    offering.offeringTimes && offering.offeringTimes.length > 0 ?
                        (() => {
                            const limits = offering.offeringTimes.flatMap(ot => ot.poolConnections.map(pc => pc?.pool?.poolLimit).filter(v => typeof v === "number" && !isNaN(v)) as number[]);
                            if (limits.length === 0) return 'No limit';
                            return limits.reduce((a, b) => a + b, 0);
                        })()
                    : (offering.poolConnections?.length > 0 ? (() => {
                        const min = Math.min(
                            ...(offering.poolConnections)
                                .map(pc => pc?.pool?.poolLimit)
                                .filter(v => typeof v === "number" && !isNaN(v)) as number[]
                        );
                        return min === Infinity ? 'No limit' : min;
                    })() : 'N/A'),
                    offering.blockPeriod && offering.blockUnit ? fmtBlockPeriod(offering.blockPeriod, offering.blockUnit) : 'N/a',

                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <Box display="flex" flexDirection="column" style={{ gap: 4 }}>
                            {offering.offeringTimes && offering.offeringTimes.length > 0 ? (
                                offering.offeringTimes.map((offeringTime: IOfferingTimeResponse) => (
                                    <Box key={offeringTime.id} display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                        <Typography variant="body2">{getTimeChipText(offeringTimeToForm(offeringTime))}</Typography>
                                        {hasEditPermission && (
                                            <IconButton size="small" onClick={() => handleDeleteTime(offeringTime.url, offering.id)}>
                                                <DeleteIcon fontSize="small" color="error" />
                                            </IconButton>
                                        )}
                                    </Box>
                                ))
                            ) : 'N/a'}
                        </Box>
                    </Box>,

                ]
            }

            const offeringAddonFields = addonFields.filter(field => isAddonForOffering(field, offering))
            row.values.push(
                offeringAddonFields.length > 0 ? (
                    <Box display="flex" flexDirection="column" style={{ gap: 4 }}>
                        {offeringAddonFields.map(field => (
                            <Typography key={field.url} variant="body2">
                                {field.fieldName} ({addonTypeLabels[field.dataType] ?? field.dataType}{field.required ? ', Required' : ''}{field.addToPrice ? ', Price' : ''})
                            </Typography>
                        ))}
                    </Box>
                ) : 'N/A'
            )

            row.values.push(
                <Box style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Typography>{(offering.isDakshinaRequired && offering.minDakshinaAmount) ? offering.minDakshinaAmount : "N/A"}</Typography>
                    {(hasEditPermission) && <FormControlLabel
                        label={""}
                        checked={offering.isDakshinaRequired ? offering.isDakshinaRequired : false}
                        onChange={() => handleToggle(offering)}
                        control={
                            <Switch
                                name={''}
                            />}
                    />}
                </Box>)
            // Token info + reset button column
            row.values.push(
                <Box style={{ display: 'flex', gap: 8, alignItems: 'center', flexDirection: 'column' }}>
                    {/* <Typography style={{ fontSize: 12 }}>{offering.tokenReferenceNo ? `#${offering.tokenReferenceNo}` : 'N/A'}</Typography> */}
                    <Typography style={{ fontSize: 11, color: '#666' }}>{offering.tokenReferenceDt ? formatDate(new Date(offering.tokenReferenceDt), 'dd MMM yyyy, hh:mm a') : ''}</Typography>
                    {hasEditPermission && offering.tokenEnabled && (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            {offering.tokenDayWise ? (
                                <KeyboardDatePicker
                                    variant="inline"
                                    format="yyyy-MM-dd"
                                    value={selectedDates[String(offering.id)] ?? null}
                                    onChange={(date) => setSelectedDates(prev => ({ ...prev, [String(offering.id)]: date ?? null }))}
                                    size="small"
                                />
                            ) : null}
                            <IconButton size='small' aria-label="reset token" onClick={async () => {
                                try {
                                    const dateObj = selectedDates[String(offering.id)]
                                    const payload: any = { offeringId: offering.id }
                                    if (offering.tokenDayWise && dateObj) {
                                        payload.date = formatDate(dateObj as Date, 'yyyy-MM-dd')
                                    }
                                    const res = await dispatch(offeringResetTokenReferenceThunk(payload))
                                    // handle result and show snackbar
                                    if (offeringResetTokenReferenceThunk.fulfilled.match(res)) {
                                        enqueueSnackbar('Token reset successfully', { variant: 'success' })
                                    } else {
                                        const err = res.payload ?? res.error
                                        enqueueSnackbar(extractError(err) ?? 'Failed to reset token', { variant: 'error' })
                                    }
                                } catch (e: any) {
                                    enqueueSnackbar(extractError(e) ?? 'Failed to reset token', { variant: 'error' })
                                }
                            }}>
                                <RefreshIcon />
                            </IconButton>
                        </div>
                    )}
                </Box>
            )
            // Courier Enabled column
            row.values.push(
                <Box style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <FormControlLabel
                        label=""
                        checked={offering.isCourierAvailable ?? false}
                        onChange={hasEditPermission ? () => handleToggleCourier(offering) : undefined}
                        disabled={!hasEditPermission}
                        control={<Switch name="" />}
                    />
                </Box>
            )
            rows.push(row)
        })
        return rows
    }, [hasEditPermission, selectedIds, onSelectOne, deityMap, addonFields, dispatch, enqueueSnackbar, selectedDates, handleDeleteTime, handleToggleCourier])

    const handleToggle = useCallback(async (offering: IOfferingResponse) => {
        await dispatch(offeringUpdateThunk({
            url: offering.url,
            data: { isDakshinaRequired: !offering.isDakshinaRequired }
        }))
    }, [dispatch])

    const rows = useMemo(() => getRows(visibleOfferings, handleToggle, categoryMap), [visibleOfferings, handleToggle, categoryMap, getRows])

    const headRow = useMemo(() => {
        const hasSelection = selectedIds.length > 0
        const rows = [
            <Box display="flex" alignItems="center">
                <Checkbox
                    size="small"
                    checked={visibleIds.length > 0 && selectedVisibleCount === visibleIds.length}
                    indeterminate={selectedVisibleCount > 0 && selectedVisibleCount < visibleIds.length}
                    onChange={event => onSelectAllVisible?.(visibleIds, event.target.checked)}
                />
                <Typography variant="body2">#</Typography>
            </Box>,
            'offering.templeRefNo',
            <HeaderAction
                label={<IntlMessages id="common.name" />}
                onClick={hasEditPermission ? onBulkEditTags : undefined}
                disabled={!hasSelection || !hasEditPermission || !onBulkEditTags}
            />,
            'offeringCategory',
            'common.price',
            'offering.assignedDeities',
            <HeaderAction
                label={<IntlMessages id="offering.offeringType" />}
                onClick={hasEditPermission ? onBulkEditTypeDates : undefined}
                disabled={!hasSelection || !hasEditPermission || !onBulkEditTypeDates}
            />,
            <HeaderAction
                label={<IntlMessages id="offering.offeringMedium" />}
                onClick={hasEditPermission ? onBulkEditMedium : undefined}
                disabled={!hasSelection || !hasEditPermission || !onBulkEditMedium}
            />,
            <HeaderAction
                label={<IntlMessages id="offeringPool.title" defaultMessage="Pools" />}
                onClick={hasEditPermission ? onBulkEditPools : undefined}
                disabled={!hasSelection || !hasEditPermission || !onBulkEditPools}
            />,
            'offering.perDayLimit',
            'offering.blockPeriod',
            <HeaderAction
                label={<IntlMessages id="offering.time" />}
                onClick={hasEditPermission ? onBulkEditTimes : undefined}
                disabled={!hasSelection || !hasEditPermission || !onBulkEditTimes}
            />,
            <HeaderAction
                label="Addon Fields"
                onClick={hasEditPermission ? onBulkEditAddonFields : undefined}
                disabled={!hasSelection || !hasEditPermission || !onBulkEditAddonFields}
            />,
            'offering.minDakshinaAmount',
            'offering.tokenInfo',
            <HeaderAction
                label="Courier Enabled"
                onClick={hasEditPermission ? onBulkEditCourier : undefined}
                disabled={!hasSelection || !hasEditPermission || !onBulkEditCourier}
            />,
        ]
        // if (hasEditPermission) rows.push('offering.isDakshinaRequired')
        return rows
    }, [visibleIds, selectedVisibleCount, onSelectAllVisible, selectedIds.length, hasEditPermission, onBulkEditTags, onBulkEditTypeDates, onBulkEditMedium, onBulkEditTimes, onBulkEditPools, onBulkEditAddonFields, onBulkEditCourier])

    const columnStyles: (React.CSSProperties | undefined)[] = useMemo(() => [
        { width: 80, minWidth: 80 },
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        { minWidth: 260, width: 260 },
        { minWidth: 220, width: 240 },
        { minWidth: 80, width: 90 },
        { minWidth: 90, width: 110 },
        { minWidth: 100, width: 130 },
    ], [])

    return (
        <>
            <MemoizedBasicTable
                rows={rows}
                headRow={headRow}
                editHandler={editHandler}
                deleteHandler={deleteHandler}
                deletingIds={deletingIds}
                columnStyles={columnStyles}
                stickyHeader
            />
            <ConfirmationDialog
                open={Boolean(deletingTime)}
                onDeny={() => setDeletingTime(null)}
                onConfirm={confirmDeleteTime}
                title="Are you sure you want to delete this offering time?"
                dialogTitle="Confirm delete"
            />
        </>
    );
};

export default OfferingTable;
